import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { LDialog, LFade, LFormInput, LButton } from "@loffa/gpp-modules";
import styles from "./AddAutoImportConnectionModal.module.css";
import { AuthContext } from "../../../../../../context/AuthContext";
import KeyBasedLButton from "../../../../../Shared/KeyBasedLButton";
const API_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

const ConnectionTypeEnum = Object.freeze({
  SELECT: -1,
  NETWORK: 1,
  FTP: 2,
  LOCAL_DIRECTORY: 3,
  SFTP: 4,
});

const connectionTypeData = [
  { value: ConnectionTypeEnum.SELECT, text: "Select a Connection Type" },
  { value: ConnectionTypeEnum.NETWORK, text: "Network" },
  { value: ConnectionTypeEnum.FTP, text: "FTP" },
  { value: ConnectionTypeEnum.LOCAL_DIRECTORY, text: "Local Directory" },
  { value: ConnectionTypeEnum.SFTP, text: "SFTP" },
];

function AddAutoImportConnectionModal({ isOpen, onClose, onSave, initialData = null }) {
  const [formData, setFormData] = useState({
    Id: null,
    ConnectionTypeEnum: ConnectionTypeEnum.SELECT,
    Description: "",
    Server: "",
    Port: 0,
    RootPath: "",
    Username: "",
    Password: "",
    UseSsl: false,
    PrivateKey: "",
    PassPhrase: "",
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const authContext = useContext(AuthContext);

  useEffect(() => {
    if (isOpen && initialData) {
      const formattedData = {
        Id: initialData.isDuplicate ? null : (initialData.id || initialData.Id),
        ConnectionTypeEnum: initialData.connectionTypeEnum || initialData.ConnectionTypeEnum,
        Description: initialData.isDuplicate
          ? `Copy of ${initialData.description || initialData.Description}`
          : (initialData.description || initialData.Description),
        Server: initialData.server || initialData.Server,
        Port: initialData.port || initialData.Port,
        RootPath: initialData.rootPath || initialData.RootPath,
        Username: initialData.username || initialData.Username,
        Password: initialData.password || initialData.Password,
        UseSsl: initialData.useSsl || initialData.UseSsl,
        PrivateKey: initialData.privateKey || initialData.PrivateKey,
        PassPhrase: initialData.passPhrase || initialData.PassPhrase
      };
      setFormData(formattedData);
    }
  }, [isOpen, initialData]);

  const resetForm = useCallback(() => {
    setFormData({
      Id: null,
      ConnectionTypeEnum: ConnectionTypeEnum.SELECT,
      Description: "",
      Server: "",
      Port: 0,
      RootPath: "",
      Username: "",
      Password: "",
      UseSsl: false,
      PrivateKey: "",
      PassPhrase: ""
    });
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const validateForm = useCallback(() => {
    if (formData.ConnectionTypeEnum === ConnectionTypeEnum.SELECT) {
      setError("Please select a connection type");
      return false;
    }
    if (!formData.Description.trim()) {
      setError("Description is required");
      return false;
    }
    if (
      (formData.ConnectionTypeEnum === ConnectionTypeEnum.NETWORK ||
        formData.ConnectionTypeEnum === ConnectionTypeEnum.FTP) &&
      (!formData.Server.trim() || !formData.Username.trim() || !formData.Password.trim())
    ) {
      setError("Server, username and password are required for network/FTP connections");
      return false;
    }
    if (!formData.RootPath.trim()) {
      setError("Root path is required");
      return false;
    }
    return true;
  }, [formData]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    setError(null);
  }, []);

  const handleConnectionTypeChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, ConnectionTypeEnum: e.value?.value }));
  }, []);

  const handleSave = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setIsSubmitting(true);
      setError(null);
      const dataToSend = {
        ...formData,
        ConnectionTypeEnum: parseInt(formData.ConnectionTypeEnum, 10)
      };
      const url = !formData.Id
        ? `${API_BASE_URL}/multitenancy/api/AutoImportConnections`
        : `${API_BASE_URL}/multitenancy/api/AutoImportConnections/${formData.Id}`;
      const response = await fetch(url, {
        method: formData.Id ? "PUT" : "POST",
        headers: {
          "Authorization": `Bearer ${authContext.authState().accessToken.accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(dataToSend)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save handler connection");
      }
      const savedData = await response.json();
      onSave(savedData);
      onClose();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  },
  [formData, authContext, onSave, onClose, resetForm, validateForm]
  );

  const selectedConnectionType = useMemo(
    () => connectionTypeData.find(item => item?.value === formData?.ConnectionTypeEnum),
    [formData.ConnectionTypeEnum]
  );

  const buttonKey = useMemo(() => JSON.stringify(formData), [formData]);

  if (!isOpen) return null;

  const modalBody = (
    <div>
      {error && (
        <div className={styles.error}>
          {error}
        </div>
      )}

      <div className={styles.fieldGroup + " " + styles.comboBoxLabelAlign}>
        <LFormInputWithRequired
          type="combobox"
          labelAlign="top"
          required
          labelName="Connection Type"
          comboBox={{
            data: connectionTypeData,
            textField: "text",
            dataItemKey: "value",
            placeholder: "Select a Connection Type",
            value: selectedConnectionType,
            onChange: handleConnectionTypeChange
          }}
        />
      </div>

      <div className={styles.fieldGroup}>
        <LFormInputWithRequired
          type="input"
          labelAlign="top"
          required
          labelName="Description"
          input={{
            name: "Description",
            placeholder: "Enter Connection Description",
            value: formData.Description,
            onChange: handleInputChange,
          }}
        />
      </div>

      {(formData.ConnectionTypeEnum === ConnectionTypeEnum.NETWORK ||
        formData.ConnectionTypeEnum === ConnectionTypeEnum.FTP ||
        formData.ConnectionTypeEnum === ConnectionTypeEnum.SFTP) && (
          <>
            <div className={styles.fieldGroup}>
              <LFormInputWithRequired
                type="input"
                labelAlign="top"
                required
                labelName="Server"
                input={{
                  name: "Server",
                  placeholder: "Enter Server/Host",
                  value: formData.Server,
                  onChange: handleInputChange,
                }}
              />
            </div>

            {(formData.ConnectionTypeEnum === ConnectionTypeEnum.FTP ||
              formData.ConnectionTypeEnum === ConnectionTypeEnum.SFTP) && (
                <div className={styles.fieldGroup}>
                  <LFormInput
                    type="input"
                    labelAlign="top"
                    labelName="Port"
                    input={{
                      name: "Port",
                      type: "number",
                      placeholder: "Enter Port",
                      value: formData.Port,
                      onChange: handleInputChange,
                    }}
                  />
                </div>
              )}
            <div className={styles.authFormInputs}>
              <div className={styles.fieldGroup}>
                <LFormInputWithRequired
                  type="input"
                  labelAlign="top"
                  required
                  labelName="Username"
                  input={{
                    name: "Username",
                    placeholder: "Enter Username",
                    value: formData.Username,
                    onChange: handleInputChange,
                  }}
                />
              </div>
              <div className={styles.fieldGroup}>
                <LFormInputWithRequired
                  type="input"
                  labelAlign="top"
                  required
                  labelName="Password"
                  input={{
                    name: "Password",
                    type: "password",
                    placeholder: "Enter Password",
                    value: formData.Password,
                    onChange: handleInputChange,
                  }}
                />
              </div>
            </div>

            {formData.ConnectionTypeEnum === ConnectionTypeEnum.FTP && (
              <div className={styles.fieldGroup + " " + styles.checkboxInputAlign}>
                <LFormInputWithRequired
                  type="checkbox"
                  labelName="Use SSL"
                  checkbox={{
                    checked: formData.UseSsl,
                    onChange: e =>
                      setFormData(prev => ({ ...prev, UseSsl: e.value })),
                  }}
                />
              </div>
            )}

            {(formData.ConnectionTypeEnum === ConnectionTypeEnum.FTP ||
              formData.ConnectionTypeEnum === ConnectionTypeEnum.SFTP) && (
                <>
                  <div className={styles.fieldGroup + " " + styles.comboBoxLabelAlign}>
                    <LFormInputWithRequired
                      type="textarea"
                      labelAlign="top"
                      labelName="Private Key"
                      required
                      input={{
                        name: "PrivateKey",
                        placeholder: "Enter Private Key",
                        value: formData.PrivateKey,
                        onChange: handleInputChange,
                      }}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <LFormInputWithRequired
                      type="input"
                      labelAlign="top"
                      labelName="Passphrase"
                      required
                      input={{
                        name: "PassPhrase",
                        type: "password",
                        placeholder: "Enter Passphrase",
                        value: formData.PassPhrase,
                        onChange: handleInputChange,
                      }}
                    />
                  </div>
                </>
              )}
          </>
        )}

      <div className={styles.fieldGroup}>
        <LFormInputWithRequired
          type="input"
          labelAlign="top"
          required
          labelName="Root Path"
          input={{
            name: "RootPath",
            placeholder: "Enter Root Path",
            value: formData.RootPath,
            onChange: handleInputChange
          }}
        />
      </div>
    </div>
  )

  return (
    <LFade transitionEnterDuration={300}>
      <LDialog
        width="700"
        title={`${initialData?.isDuplicate ? "Duplicate" : (initialData ? "Edit" : "Add")} Handler Connection`}
        subtitle={<div className={styles.modalSubtitle}>Required Fields</div>}
        body={modalBody}
        actionLayout="end"
        action={
          <>
            <LButton onClick={handleClose} type="button">
              Cancel
            </LButton>
            <KeyBasedLButton
              parentKey={buttonKey}
              buttonText={isSubmitting ? 'Saving...' : (initialData?.isDuplicate ? 'Create Copy' : (formData.Id ? 'Update' : 'Save'))}
              disabled={isSubmitting}
              themeColor="primary"
              className={`${styles.saveButton}`}
              onClick={handleSave}
            />
          </>
        }
        actionCloseButton={handleClose}
        actionOverlay={handleClose}
      >
      </LDialog>
    </LFade>
  );
}

const LFormInputWithRequired = ({ labelName, required, ...rest }) => {
  const modifiedLabel = required ? (
    <>
      {labelName} <span style={{ color: "red" }}>*</span>
    </>
  ) : (
    labelName
  );

  return <LFormInput labelName={modifiedLabel} required={required} {...rest} />;
};

export { AddAutoImportConnectionModal };